import React from 'react';
import { withNamespaces } from 'react-i18next';
import { analytics } from 'core/analytics';
import { action, computed, observable } from 'mobx';
import { observer } from 'mobx-react';

export class ErrorState {
  @observable error: Error | null = null;

  @observable errorCleared = false;

  @observable errorClearedAttempts = 0;

  @action SetError = (error: Error) => {
    this.error = error;
  };

  @action ClearError = () => {
    this.error = null;
    this.errorClearedAttempts += 1;
    this.errorCleared = true;
    // After 60 seconds set errorCleared to false.
    // The strategy here is that if either:
    //    a) The error is in an infinite loop
    //    b) The error is retriggred by the user in a short period of time
    // We don't want to keep trying to reload the component and our best strategy is for the user to refresh.
    // If, on the other hand, the error is not being triggered repeatedly,
    // it might be fine to allow the user to just try to recover again.
    setTimeout(() => {
      this.errorCleared = false;
    }, 60000);
  };

  @computed get key() {
    /* Each time we ClearError change the key to attmpt to reload the component */
    return this.errorClearedAttempts;
  }
}

export const ErrorRecoveryMessage = observer(
  ({
    typeName,
    tryRecover,
    errorState,
  }: {
    typeName: string;
    tryRecover?: () => void;
    errorState: ErrorState;
  }) => {
    if (!tryRecover) {
      // eslint-disable-next-line no-param-reassign
      tryRecover = () => errorState.ClearError();
    }
    let message = (
      <>
        Oops! There was a problem with {typeName}.&nbsp;
        <a
          href={window.location.href}
          onClick={e => {
            tryRecover();
            e.preventDefault();
          }}
        >
          Try again
        </a>
        .
      </>
    );
    if (errorState.errorCleared) {
      message = (
        <>
          Sorry! We couldn&apos;t recover from an error in {typeName}.
          Please&nbsp;
          <a href={window.location.href} onClick={window.location.reload}>
            refresh
          </a>{' '}
          &nbsp; to try again.
        </>
      );
    }
    return <>{message}</>;
  },
);

class ErrorHandlerInner extends React.Component {
  errorState: ErrorState;

  props: any;

  constructor(props: any) {
    super(props);
    this.errorState = new ErrorState();
  }

  static getDerivedStateFromError() {
    /* This is a no-op since we use Mobx for a slightly more complex error handling.
    Is it dangerous to not implement this for some reason?
    It is included here to not show a React warning in the console. */
    // Update state so the next render will show the fallback UI.
    // return { hasError: error };
    return {};
  }

  componentDidCatch(error: Error) {
    this.errorState.SetError(error);
    analytics.logError(error);
  }

  render() {
    if (this.errorState.error) {
      const { ErrorComponent } = this.props;
      return <ErrorComponent {...this.props} errorState={this.errorState} />;
    }
    return (
      <React.Fragment key={this.errorState.key}>
        {this.props.children}
      </React.Fragment>
    );
  }
}

export const ErrorHandler = withNamespaces()(observer(ErrorHandlerInner));
